import { ApiError } from "src/api";
import { path, isString, isArray, isObject } from 'lodash/fp';
import { useCallback, useMemo, useState } from "react";
import { ToastOptions } from "react-toastify";
import toast from '../utils/toaster';
import { FaClipboard } from 'react-icons/fa';

const isApiError = (error: unknown): error is ApiError => {
    return error instanceof ApiError && error.name === 'ApiError';
}

const isError = (error: unknown): error is Error => {
    return !!error && typeof error === 'object' && 'message' in error && isString('message', error);
}

function ErrorToast({ messages = [] }: { messages: string[] }) {
    const [copied, setCopied] = useState(false);

    const isClipboardPresent = useMemo(() => {
        return Boolean(path('clipboard.writeText', navigator));
    }, [])

    const copyToClipboard = useCallback(async () => {
        if (!isClipboardPresent) return;

        try {
            await navigator.clipboard.writeText(messages.join('\n'));
            setCopied(true);
        } catch (error) {
            console.debug(error)
        }
    }, [isClipboardPresent, messages]);

    const lasteMessage = useMemo(() => {
        return [...messages].pop()
    }, [messages])


    return (
        <div style={{ display: "flex", alignItems: "center", flexDirection: "column", fontSize: "17px" }}>
            <div style={{ overflowWrap: "break-word", wordBreak: "break-word", whiteSpace: "pre-wrap", flex: 1 }}>{lasteMessage}</div>
            <div
                className="d-flex mt-auto align-items-center gap-2"
                style={{
                    width: "100%",
                    justifyContent: "flex-end",
                }}
            >
                <span style={{ visibility: copied ? "visible" : "hidden", minWidth: "60px", marginTop: "5px" }}>
                    Copied!
                </span>                {isClipboardPresent && (
                    <FaClipboard
                        style={{ cursor: "pointer", fontSize: "23px" }}
                        onClick={copyToClipboard}
                    />
                )}
            </div>
        </div>
    )
}

export function useErrorToast(options: ToastOptions = {}) {

    const defaultOptions = useMemo(() => ({
        autoClose: 3000,
        closeOnClick: false,
        closeButton: true,
        bodyStyle: { fontSize: 12 },
        ...options
    }), [options])

    const showError = useCallback((error: any, defaultMessage: string) => {
        let messages = parseErrorMessages(error, defaultMessage);

        if (!messages.length) messages = [defaultMessage];

        console.debug(messages)
        toast.error(
            () => <ErrorToast messages={messages} />,
            { ...defaultOptions }
        )
    }, [defaultOptions])

    return { showError }
}


function parseErrorMessages(error: any, defaultMessage: string): string[] {
    if (isString(error)) return [error];

    if (!error) return [defaultMessage];

    if (isApiError(error)) return parseApiErrorMessages(error);

    if (isError(error)) return [error.message];

    return [String(error)];
}

function parseApiErrorMessages(error: ApiError): string[] {
    let lines: string[] = [];

    try {
        if (!!error?.request?.url) {
            lines.push(`Request: ${error?.request?.url}`);
        }

        if (!!error.statusText) {
            lines.push(`Status:  ${error.status || ''} ${error.statusText || 'Unknown'}`);
        }

        const errId = path('errorId', error?.body);

        if (!!errId) {
            lines.push(`Error ID: ${errId}`);
        }

        const errMessage = path('message', error?.body);

        if (!!errMessage && isString(errMessage)) {
            lines.push(`Error: ${errMessage}`);
            return lines;
        }

        const errMessageList: any[] = path('messageList', error?.body);

        if (!isArray(errMessageList)) {
            errMessageList && lines.push(String(errMessageList));
            return lines;
        }

        if (!errMessageList.length) {
            return lines;
        }

        for (const err of errMessageList) {
            if (isObject(err) && path('message', err)) {
                lines.push(path('message', err))
                continue;
            }

            if (!isString(err)) {
                lines.push(String(err))
                continue;
            }

            try {
                const parsed = path('message', JSON.parse(err))
                parsed && lines.push(parsed);
            } catch (pError) {
                lines.push(err)
            }
        }
    } catch (error) {
        console.warn('Error parser failure:', error)
    }

    return lines;
}
